import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import api, { gymSettings } from '../services/api';
import { ThemeSelector } from './ThemeToggle';
import LanguageSelector from './LanguageSelector';
import TemplateEditor from './TemplateEditor';
import './SettingsPage.css';

// O contrato do MagSend é explícito: consultar o estado de 3 em 3 segundos, no máximo 2
// minutos, e nunca pedir QR a cada consulta.
const WA_POLL_MS = 3000;
const WA_POLL_LIMIT_MS = 2 * 60 * 1000;

const SettingsPageFull = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('gym');
  const [wa, setWa] = useState({ state: 'desconhecido', ok: false, qrCode: null, pairingCode: null, error: null });
  const [waPairingNumber, setWaPairingNumber] = useState('');
  const [waInbound, setWaInbound] = useState(null);
  const [waBusy, setWaBusy] = useState(false);
  const waPollUntil = React.useRef(0);
  const [settings, setSettings] = useState(null);
  const [company, setCompany] = useState(null);
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [iconType, setIconType] = useState('emoji');
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [showLayoutDesigner, setShowLayoutDesigner] = useState(false);
  const [layoutForm, setLayoutForm] = useState(null);

  const [calendarConnected, setCalendarConnected] = useState(false);
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    bookingReminders: true,
    promotions: false,
    weeklyDigest: true
  });

  const emojiOptions = ['💪', '🏋️', '🏃', '🎯', '⚡', '🔥', '🌟', '🏆', '💎', '🎪', '🏠', '🏢'];

  const tabs = [
    { id: 'gym', label: '🏋️ Ginásio', desc: 'Aparência & Reservas' },
    { id: 'company', label: '🏢 Empresa', desc: 'Dados para Faturação' },
    { id: 'email', label: '📧 Email / SMTP', desc: 'Envio de emails' },
    { id: 'email-templates', label: '📨 Templates Email', desc: 'Emails Personalizados', show: () => company?.smtpEnabled },
    { id: 'sms', label: '📱 SMS', desc: 'Envio de SMS' },
    { id: 'sms-templates', label: '💬 Templates SMS', desc: 'Mensagens Personalizadas', show: () => company?.smsEnabled },
    { id: 'sms-params', label: '⚙️ Parâmetros SMS', desc: 'Regras de Envio', show: () => company?.smsEnabled },
    { id: 'layouts', label: '📐 Layouts', desc: 'Layouts de Documentos' },
    { id: 'notifications', label: '🔔 Notificações', desc: 'Preferências de notificações' },
    { id: 'account', label: '👤 Conta', desc: 'Dados da conta' },
  ];

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [gymRes, compRes, layoutsRes] = await Promise.all([
        gymSettings.get(),
        api.get('/gymsettings/company').catch(() => ({ data: null })),
        api.get('/billing/layouts').catch(() => ({ data: [] }))
      ]);
      
      setSettings(gymRes.data);
      if (gymRes.data?.gymIcon && (gymRes.data.gymIcon.startsWith('http') || gymRes.data.gymIcon.startsWith('data:'))) {
        setIconType('image');
      }
      
      setCompany(compRes.data || {
        companyName: '', tradeName: '', nif: '', address: '', postalCode: '', city: '', country: 'Portugal',
        phone: '', email: '', website: '', shareCapital: '', commercialRegistry: '', registryNumber: '',
        iban: '', bic: '', logoUrl: '', invoiceFooterText: '', legalDisclaimer: '',
        atCertificateNumber: '', atSoftwareNumber: '',
        smtpHost: '', smtpPort: 587, smtpUser: '', smtpPassword: '', smtpFromEmail: '', smtpFromName: '',
        smtpEnableSsl: true, smtpEnabled: false,
        smsSenderId: '', smsEnabled: false
      });
      
      setLayouts(layoutsRes.data || []);
    } catch (error) {
      setSettings({ gymName: 'GyMag', gymIcon: '💪', maxBookingsPerUser: 3, maxDaysInAdvance: 7, cancellationDeadlineHours: 2 });
      setCompany({});
    } finally {
      setLoading(false);
    }
  };

  const resizeImage = (file, maxSize = 64) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = maxSize;
          canvas.height = maxSize;
          const ctx = canvas.getContext('2d');
          const size = Math.min(img.width, img.height);
          const x = (img.width - size) / 2;
          const y = (img.height - size) / 2;
          ctx.drawImage(img, x, y, size, size, 0, 0, maxSize, maxSize);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const resizeLogo = (file, maxSize = 300) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/png', 0.9));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const resizedImage = await resizeImage(file, 64);
        setSettings({ ...settings, gymIcon: resizedImage });
        toast.success('Imagem redimensionada para 64x64 pixels');
      } catch { toast.error('Erro ao processar imagem'); }
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const resized = await resizeLogo(file, 300);
        setCompany({ ...company, logoUrl: resized });
        toast.success('Logotipo carregado');
      } catch { toast.error('Erro ao processar logotipo'); }
    }
  };

  const loadWhatsAppStatus = React.useCallback(async () => {
    try {
      const res = await api.get('/whatsapp/status');
      const c = res.data?.connection || {};
      setWa(prev => ({ ...prev, ok: !!c.ok, state: c.state || 'indisponível', error: c.error || null }));
      return c.state;
    } catch {
      setWa(prev => ({ ...prev, ok: false, state: 'indisponível' }));
      return 'indisponível';
    }
  }, []);

  useEffect(() => { loadWhatsAppStatus(); }, [loadWhatsAppStatus]);

  // Enquanto o número não está ligado, acompanha-se o estado — mas com fim à vista: a janela
  // fecha ao ligar, ao sair do ecrã, ou ao fim de dois minutos.
  useEffect(() => {
    if (waPollUntil.current === 0 || wa.state === 'open') return undefined;
    const timer = setInterval(async () => {
      if (Date.now() > waPollUntil.current) {
        waPollUntil.current = 0;
        clearInterval(timer);
        return;
      }
      const state = await loadWhatsAppStatus();
      if (state === 'open') {
        waPollUntil.current = 0;
        clearInterval(timer);
        setWa(prev => ({ ...prev, qrCode: null, pairingCode: null }));   // credenciais temporárias: saem do ecrã ao ligar
        toast.success('WhatsApp ligado!');
      }
    }, WA_POLL_MS);
    return () => clearInterval(timer);
  }, [wa.state, loadWhatsAppStatus]);

  const handleWhatsAppConnect = async () => {
    setWaBusy(true);
    try {
      const res = await api.post('/whatsapp/connect');
      const d = res.data || {};
      setWa({ ok: !!d.ok, state: d.state || 'indisponível', qrCode: d.qrCode || null, error: d.error || null });
      if (d.state === 'open') toast.success('WhatsApp já está ligado');
      else if (d.qrCode) toast.info('Lê o código no telemóvel: WhatsApp → Dispositivos ligados');
      else toast.info('A preparar a ligação. Carrega em "Atualizar QR" daqui a uns segundos.');
      waPollUntil.current = Date.now() + WA_POLL_LIMIT_MS;
    } catch {
      toast.error('Não foi possível iniciar a ligação do WhatsApp');
    } finally {
      setWaBusy(false);
    }
  };

  // Quem está a ver este ecrã no telemóvel que quer ligar não consegue fotografar o próprio
  // QR. O MagSend devolve então um código para escrever no WhatsApp.
  const handleWhatsAppPairing = async () => {
    if (!waPairingNumber.trim()) { toast.error('Escreve o número com indicativo (ex.: 351912345678)'); return; }
    setWaBusy(true);
    try {
      const res = await api.post('/whatsapp/connect', { pairingNumber: waPairingNumber.trim() });
      const d = res.data || {};
      setWa({ ok: !!d.ok, state: d.state || 'indisponível', qrCode: d.qrCode || null, pairingCode: d.pairingCode || null, error: d.error || null });
      if (d.pairingCode) toast.info('Escreve o código no telemóvel: WhatsApp → Dispositivos ligados → Ligar com número');
      else if (d.state === 'open') toast.success('WhatsApp já está ligado');
      else toast.info('Sem código por agora. Tenta o QR.');
      waPollUntil.current = Date.now() + WA_POLL_LIMIT_MS;
    } catch {
      toast.error('Não foi possível pedir o código de ligação');
    } finally {
      setWaBusy(false);
    }
  };

  const loadInbound = React.useCallback(async () => {
    try {
      const res = await api.get('/whatsapp/inbound');
      setWaInbound(res.data || null);
    } catch {
      setWaInbound(null);
    }
  }, []);

  useEffect(() => { loadInbound(); }, [loadInbound]);

  const handleRegisterInbound = async (rotate = false) => {
    setWaBusy(true);
    try {
      const res = await api.post('/whatsapp/inbound/register', { rotate });
      const d = res.data || {};
      if (d.warning) toast.warning(d.warning);
      else toast.success('Receção activada');
      await loadInbound();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível activar a receção');
    } finally {
      setWaBusy(false);
    }
  };

  const handleWhatsAppQrCode = async () => {
    setWaBusy(true);
    try {
      const res = await api.get('/whatsapp/qrcode');
      const d = res.data || {};
      setWa(prev => ({ ...prev, ok: !!d.ok, state: d.state || prev.state, qrCode: d.qrCode || null, error: d.error || null }));
      if (!d.qrCode) toast.info(d.state === 'open' ? 'Já está ligado.' : 'Ainda não há QR. Tenta daqui a uns segundos.');
      waPollUntil.current = Date.now() + WA_POLL_LIMIT_MS;
    } catch {
      toast.error('Não foi possível obter o código');
    } finally {
      setWaBusy(false);
    }
  };

  const handleSaveGym = async () => {
    setSaving(true);
    try {
      const saved = await gymSettings.update(settings);
      // O servidor impõe limites (ex.: antecedência dos lembretes 15..10080): mostra-se o valor que ficou.
      if (saved?.data) setSettings(saved.data);
      toast.success('Configurações do ginásio guardadas!');
      localStorage.setItem('siteSettings', JSON.stringify({ name: settings.gymName, icon: settings.gymIcon }));
      window.dispatchEvent(new Event('settingsUpdated'));
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      await api.put('/gymsettings/company', company);
      toast.success('Dados da empresa guardados!');
      await loadAll(); // Reload to update tabs visibility
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const handleTestEmail = async () => {
    if (!testEmail) { toast.error('Insira um email para teste'); return; }
    setTesting(true);
    try {
      await api.put('/gymsettings/company', company);
      const res = await api.post('/gymsettings/company/test-email', { email: testEmail });
      toast.success(res.data?.message || 'Email de teste enviado!');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao enviar email de teste');
    }
    setTesting(false);
  };

  const handleToggleNotification = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
    toast.success('Preferência atualizada');
  };

  const renderIconPreview = () => {
    if (settings?.gymIcon && (settings.gymIcon.startsWith('http') || settings.gymIcon.startsWith('data:'))) {
      return <img src={settings.gymIcon} alt="Preview" style={{ width: '48px', height: '48px', borderRadius: '10px', objectFit: 'cover' }} />;
    }
    return <span style={{ fontSize: '48px' }}>{settings?.gymIcon || '💪'}</span>; 
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const ST = {
    wrap: { display: 'flex', gap: '0', minHeight: '600px', background: '#fff', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--border-color)' },
    sidebar: { width: '220px', minWidth: '180px', borderRight: '1px solid var(--border-color)', background: 'var(--bg-main)', padding: '12px 0' },
    sideBtn: { display: 'flex', flexDirection: 'column', width: '100%', padding: '12px 16px', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', fontSize: '13px', background: 'transparent' },
    sideBtnActive: { background: 'var(--primary-light, #eef2ff)', borderRight: '3px solid var(--primary, #6366f1)' },
    sideBtnLabel: { fontWeight: '600', color: 'var(--text-primary)' },
    sideBtnDesc: { fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' },
    content: { flex: 1, padding: '24px', overflowY: 'auto', maxHeight: '80vh' },
    section: { marginBottom: '28px' },
    sectionTitle: { fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px' },
    field: { display: 'flex', flexDirection: 'column', gap: '4px' },
    label: { fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' },
    input: { padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '13px', background: 'var(--bg-main)' },
    textarea: { padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '13px', background: 'var(--bg-main)', minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' },
    toggle: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
    badge: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
    divider: { border: 'none', borderTop: '1px solid var(--border-color)', margin: '20px 0' },
    saveBtn: { marginTop: '20px' },
    logoPreview: { width: '120px', height: '80px', objectFit: 'contain', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '4px', background: '#fff' }
  };

  const visibleTabs = tabs.filter(tab => !tab.show || tab.show());

  return (
    <div className="data-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={ST.wrap}>
        {/* Sidebar */}
        <div style={ST.sidebar}>
          <div style={{ padding: '0 16px 12px', fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)' }}>⚙️ Configurações</div>
          {visibleTabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ ...ST.sideBtn, ...(activeTab === t.id ? ST.sideBtnActive : {}) }}>
              <span style={ST.sideBtnLabel}>{t.label}</span>
              <span style={ST.sideBtnDesc}>{t.desc}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={ST.content}>
          
          {/* GINÁSIO TAB */}
          {activeTab === 'gym' && (
            <div>
              <h3 style={ST.sectionTitle}>🎨 Aparência do Site</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px', padding: '16px', background: 'var(--bg-main)', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '12px' }}>
                  {renderIconPreview()}
                </div>
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>{settings?.gymName || 'GyMag'}</strong>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>Pré-visualização do logo</p>
                </div>
              </div>
              <div className="form-group">
                <label>Tipo de Ícone</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" className={`btn ${iconType === 'emoji' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setIconType('emoji')}>😀 Emoji</button>
                  <button type="button" className={`btn ${iconType === 'image' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setIconType('image')}>🖼️ Imagem</button>
                </div>
              </div>
              {iconType === 'emoji' ? (
                <div className="form-group">
                  <label>Escolher Emoji</label>
                  <div className="emoji-picker">
                    {emojiOptions.map(emoji => (
                      <button key={emoji} type="button" className={`emoji-btn ${settings?.gymIcon === emoji ? 'active' : ''}`}
                        onClick={() => setSettings({ ...settings, gymIcon: emoji })}>{emoji}</button>
                    ))}
                  </div>
                  <small style={{ color: 'var(--text-secondary)', marginTop: '8px', display: 'block' }}>Ou escreva o seu próprio emoji:</small>
                  <input type="text" className="form-input"
                    value={settings?.gymIcon?.startsWith('data:') || settings?.gymIcon?.startsWith('http') ? '' : (settings?.gymIcon || '💪')}
                    onChange={(e) => setSettings({ ...settings, gymIcon: e.target.value })} maxLength="4"
                    style={{ width: '80px', textAlign: 'center', fontSize: '24px' }} />
                </div>
              ) : (
                <div className="form-group">
                  <label>Carregar Imagem</label>
                  <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} id="icon-upload" />
                  <label htmlFor="icon-upload" className="btn btn-outline" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', width: 'fit-content' }}>📁 Escolher Ficheiro</label>
                  {settings?.gymIcon && (settings.gymIcon.startsWith('data:') || settings.gymIcon.startsWith('http')) && (
                    <button type="button" className="btn btn-outline" onClick={() => setSettings({ ...settings, gymIcon: '💪' })} style={{ width: 'fit-content', marginTop: '8px' }}>🗑️ Remover Imagem</button>
                  )}
                </div>
              )}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>Nome do Site</label>
                <input type="text" className="form-input" value={settings?.gymName || ''}
                  onChange={(e) => setSettings({ ...settings, gymName: e.target.value })} placeholder="Ex: GyMag, FitClub, etc." />
              </div>
              <hr style={ST.divider} />
              <h3 style={ST.sectionTitle}>⚙️ Regras de Reservas</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Máx. Reservas por Utilizador</label>
                  <input type="number" className="form-input" value={settings?.maxBookingsPerUser || 3}
                    onChange={(e) => setSettings({ ...settings, maxBookingsPerUser: parseInt(e.target.value) })} min="1" />
                </div>
                <div className="form-group">
                  <label>Dias Antecedência</label>
                  <input type="number" className="form-input" value={settings?.maxDaysInAdvance || 7}
                    onChange={(e) => setSettings({ ...settings, maxDaysInAdvance: parseInt(e.target.value) })} min="1" />
                </div>
                <div className="form-group">
                  <label>Horas para Cancelamento</label>
                  <input type="number" className="form-input" value={settings?.cancellationDeadlineHours || 2}
                    onChange={(e) => setSettings({ ...settings, cancellationDeadlineHours: parseInt(e.target.value) })} min="0" />
                </div>
              </div>
              <hr style={ST.divider} />
              <h3 style={ST.sectionTitle}>📱 Ligação do WhatsApp</h3>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ marginBottom: '8px' }}>
                  Estado: <strong>{{
                    open: 'ligado',
                    connecting: 'à espera da leitura do código',
                    closed: 'desligado',
                    // Três casos que pedem coisas diferentes: um botão, o operador, ou esperar.
                    'not-connected': 'por ligar',
                    'not-configured': 'MagSend por configurar neste ginásio',
                  }[wa.state] || 'indisponível'}</strong>
                  {wa.error && wa.state !== 'open' && wa.state !== 'not-connected' && (
                    <span style={{ color: 'var(--text-secondary)' }}> ({wa.error})</span>
                  )}
                </div>
                <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '10px' }}>
                  {wa.state === 'not-configured'
                    ? 'Este ginásio ainda não tem projecto e chave próprios no MagSend. Fala com quem gere o portal — enquanto não os tiver, não há envio nem recepção.'
                    : 'O número do ginásio liga-se aqui: carrega em Ligar, e depois lê o código no telemóvel em WhatsApp → Definições → Dispositivos ligados. O código é temporário e desaparece assim que a ligação abrir.'}
                </small>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" onClick={handleWhatsAppConnect}
                    disabled={waBusy || wa.state === 'not-configured'}>
                    {waBusy ? 'A preparar...' : (wa.state === 'open' ? 'Verificar ligação' : 'Ligar WhatsApp')}
                  </button>
                  {wa.state !== 'open' && (
                    <button className="btn btn-outline" onClick={handleWhatsAppQrCode} disabled={waBusy}>
                      Atualizar QR
                    </button>
                  )}
                </div>
                {wa.qrCode && wa.state !== 'open' && (
                  <div style={{ marginTop: '12px' }}>
                    <img src={wa.qrCode} alt="Código para ligar o WhatsApp"
                      style={{ width: '240px', height: '240px', border: '1px solid var(--border-color)', borderRadius: '8px' }} />
                  </div>
                )}

                {wa.state !== 'open' && (
                  <div style={{ marginTop: '16px', padding: '12px', border: '1px dashed var(--border-color)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Sem conseguir ler o QR?</div>
                    <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                      Se estás a ver este ecrã no próprio telemóvel que queres ligar, pede um código
                      e escreve-o em WhatsApp → Dispositivos ligados → Ligar com número de telefone.
                    </small>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <input className="form-input" style={{ maxWidth: '220px' }} value={waPairingNumber}
                        onChange={(e) => setWaPairingNumber(e.target.value)} placeholder="351912345678" />
                      <button className="btn btn-outline" onClick={handleWhatsAppPairing} disabled={waBusy}>
                        Pedir código
                      </button>
                    </div>
                    {wa.pairingCode && (
                      <div style={{ marginTop: '10px', fontSize: '22px', fontWeight: 700, letterSpacing: '2px' }}>
                        {wa.pairingCode}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', background: 'var(--bg-secondary)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                    Receber mensagens {waInbound?.configured && waInbound?.secretStored
                      ? <span style={{ color: 'var(--success)' }}>— activa</span>
                      : <span style={{ color: 'var(--text-secondary)' }}>— por activar</span>}
                  </div>
                  <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                    Sem isto, o ginásio envia mas não recebe: o assistente nunca chega a ver as
                    mensagens dos membros. Activar diz ao MagSend para onde as entregar.
                  </small>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button className="btn btn-outline" onClick={() => handleRegisterInbound(false)} disabled={waBusy}>
                      {waInbound?.configured ? 'Reconfirmar receção' : 'Activar receção'}
                    </button>
                    {waInbound?.configured && !waInbound?.secretStored && (
                      <button className="btn btn-outline" onClick={() => handleRegisterInbound(true)} disabled={waBusy}>
                        Gerar novo secret
                      </button>
                    )}
                  </div>
                  {waInbound?.callbackError && (
                    <small style={{ color: 'var(--error)', display: 'block', marginTop: '8px' }}>{waInbound.callbackError}</small>
                  )}
                </div>
              </div>

              <hr style={ST.divider} />
              <h3 style={ST.sectionTitle}>🤖 Assistente de WhatsApp</h3>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!settings?.whatsAppAssistantEnabled}
                    onChange={(e) => setSettings({ ...settings, whatsAppAssistantEnabled: e.target.checked })} />
                  Responder automaticamente às mensagens de WhatsApp dos membros
                </label>
                <small style={{ color: 'var(--text-secondary)', marginTop: '8px', display: 'block' }}>
                  Quando ativo, o assistente responde a dúvidas e permite marcar/cancelar aulas por WhatsApp.
                  Requer o WhatsApp do ginásio ligado (acima). Desligado = nenhuma resposta automática.
                </small>
              </div>
              <hr style={ST.divider} />
              <h3 style={ST.sectionTitle}>⏰ Lembretes de marcação por WhatsApp</h3>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!settings?.whatsAppBookingReminderEnabled}
                    onChange={(e) => setSettings({ ...settings, whatsAppBookingReminderEnabled: e.target.checked })} />
                  Avisar os membros por WhatsApp antes da aula
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <label htmlFor="wa-reminder-minutes">Com antecedência de</label>
                  <input id="wa-reminder-minutes" type="number" min={15} max={10080} step={5}
                    style={{ width: '100px' }}
                    value={settings?.whatsAppBookingReminderMinutes ?? 60}
                    disabled={!settings?.whatsAppBookingReminderEnabled}
                    onChange={(e) => setSettings({ ...settings, whatsAppBookingReminderMinutes: parseInt(e.target.value, 10) || 60 })} />
                  <span>minutos</span>
                </div>
                <small style={{ color: 'var(--text-secondary)', marginTop: '8px', display: 'block' }}>
                  Cada marcação recebe um único aviso, à hora do ginásio. Quem marca já depois do momento do aviso
                  não recebe nenhum. Só recebem os membros que aceitam notificações por WhatsApp.
                  Requer o WhatsApp do ginásio ligado (acima). Entre 15 minutos e 7 dias; desligado por omissão.
                </small>
              </div>
              <button className="btn btn-primary" onClick={handleSaveGym} disabled={saving} style={ST.saveBtn}>
                {saving ? 'A guardar...' : '💾 Guardar Alterações'}
              </button>
            </div>
          )}

          {/* EMPRESA TAB */}
          {activeTab === 'company' && company && (
            <div>
              <h3 style={ST.sectionTitle}>🏢 Dados da Empresa</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Estes dados são usados nas faturas, recibos e documentos legais.
              </p>

              {/* Logo */}
              <div style={ST.section}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px' }}>🖼️ Logotipo</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '16px', background: 'var(--bg-main)', borderRadius: '12px' }}>
                  {company.logoUrl ? (
                    <img src={company.logoUrl} alt="Logo" style={ST.logoPreview} />
                  ) : (
                    <div style={{ ...ST.logoPreview, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '12px' }}>Sem logo</div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} id="logo-upload" />
                    <label htmlFor="logo-upload" className="btn btn-outline" style={{ cursor: 'pointer', width: 'fit-content' }}>📁 Carregar Logotipo</label>
                    {company.logoUrl && (
                      <button type="button" className="btn btn-outline" onClick={() => setCompany({ ...company, logoUrl: '' })} style={{ width: 'fit-content' }}>🗑️ Remover</button>
                    )}
                    <small style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>Recomendado: PNG transparente, max 300x300px</small>
                  </div>
                </div>
              </div>

              {/* Identification */}
              <div style={ST.section}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px' }}>📋 Identificação</div>
                <div style={ST.grid}>
                  <div style={ST.field}>
                    <label style={ST.label}>Nome da Empresa *</label>
                    <input style={ST.input} value={company.companyName || ''} onChange={e => setCompany({ ...company, companyName: e.target.value })} placeholder="Ex: FitGym, Lda." />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Nome Comercial</label>
                    <input style={ST.input} value={company.tradeName || ''} onChange={e => setCompany({ ...company, tradeName: e.target.value })} placeholder="Nome fantasia" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>NIF / NIPC *</label>
                    <input style={ST.input} value={company.nif || ''} onChange={e => setCompany({ ...company, nif: e.target.value })} placeholder="999 999 999" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Telefone</label>
                    <input style={ST.input} value={company.phone || ''} onChange={e => setCompany({ ...company, phone: e.target.value })} placeholder="+351 xxx xxx xxx" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Email</label>
                    <input style={ST.input} type="email" value={company.email || ''} onChange={e => setCompany({ ...company, email: e.target.value })} placeholder="geral@empresa.pt" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Website</label>
                    <input style={ST.input} value={company.website || ''} onChange={e => setCompany({ ...company, website: e.target.value })} placeholder="https://www.empresa.pt" />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div style={ST.section}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px' }}>📍 Morada</div>
                <div style={ST.grid}>
                  <div style={{ ...ST.field, gridColumn: '1 / -1' }}>
                    <label style={ST.label}>Morada</label>
                    <input style={ST.input} value={company.address || ''} onChange={e => setCompany({ ...company, address: e.target.value })} placeholder="Rua, número, andar..." />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Código Postal</label>
                    <input style={ST.input} value={company.postalCode || ''} onChange={e => setCompany({ ...company, postalCode: e.target.value })} placeholder="1000-001" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Cidade</label>
                    <input style={ST.input} value={company.city || ''} onChange={e => setCompany({ ...company, city: e.target.value })} placeholder="Lisboa" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>País</label>
                    <input style={ST.input} value={company.country || 'Portugal'} onChange={e => setCompany({ ...company, country: e.target.value })} />
                  </div>
                </div>
              </div>

              {/* Financial Data */}
              <div style={ST.section}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px' }}>💰 Dados Financeiros</div>
                <div style={ST.grid}>
                  <div style={ST.field}>
                    <label style={ST.label}>Capital Social</label>
                    <input style={ST.input} value={company.shareCapital || ''} onChange={e => setCompany({ ...company, shareCapital: e.target.value })} placeholder="50.000,00 €" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Conservatória</label>
                    <input style={ST.input} value={company.commercialRegistry || ''} onChange={e => setCompany({ ...company, commercialRegistry: e.target.value })} placeholder="Conservatória do Registo Comercial" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Nº Matrícula</label>
                    <input style={ST.input} value={company.registryNumber || ''} onChange={e => setCompany({ ...company, registryNumber: e.target.value })} placeholder="Matrícula" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>IBAN</label>
                    <input style={ST.input} value={company.iban || ''} onChange={e => setCompany({ ...company, iban: e.target.value })} placeholder="PT50 0000 0000 0000 0000 0000 0" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>BIC / SWIFT</label>
                    <input style={ST.input} value={company.bic || ''} onChange={e => setCompany({ ...company, bic: e.target.value })} placeholder="BPIAPTPL" />
                  </div>
                </div>
              </div>

              {/* AT Certification */}
              <div style={ST.section}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px' }}>🏛️ Certificação AT</div>
                <div style={ST.grid}>
                  <div style={ST.field}>
                    <label style={ST.label}>Nº Certificado AT</label>
                    <input style={ST.input} value={company.atCertificateNumber || ''} onChange={e => setCompany({ ...company, atCertificateNumber: e.target.value })} placeholder="Nº do certificado" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Nº Software AT</label>
                    <input style={ST.input} value={company.atSoftwareNumber || ''} onChange={e => setCompany({ ...company, atSoftwareNumber: e.target.value })} placeholder="Nº do software" />
                  </div>
                </div>
              </div>

              {/* Legal Texts */}
              <div style={ST.section}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px' }}>📝 Textos de Documentos</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={ST.field}>
                    <label style={ST.label}>Rodapé das Faturas</label>
                    <textarea style={ST.textarea} value={company.invoiceFooterText || ''} onChange={e => setCompany({ ...company, invoiceFooterText: e.target.value })}
                      placeholder="Documento processado por computador. Obrigado pela preferência." />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Menção Legal (ex: isenção IVA)</label>
                    <textarea style={ST.textarea} value={company.legalDisclaimer || ''} onChange={e => setCompany({ ...company, legalDisclaimer: e.target.value })}
                      placeholder="Isento de IVA ao abrigo do artigo 53.º do CIVA..." />
                  </div>
                </div>
              </div>

              <button className="btn btn-primary" onClick={handleSaveCompany} disabled={saving} style={ST.saveBtn}>
                {saving ? 'A guardar...' : '💾 Guardar Dados da Empresa'}
              </button>
            </div>
          )}

          {/* EMAIL TAB */}
          {activeTab === 'email' && company && (
            <div>
              <h3 style={ST.sectionTitle}>📧 Configurações de Email (SMTP)</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Configure o servidor SMTP para enviar emails pela aplicação.
              </p>

              {/* Enable toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', borderRadius: '10px', marginBottom: '20px',
                background: company.smtpEnabled ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.05)',
                border: `1px solid ${company.smtpEnabled ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.2)'}` }}>
                <label style={{ ...ST.toggle, flex: 1 }}>
                  <input type="checkbox" checked={company.smtpEnabled || false}
                    onChange={e => setCompany({ ...company, smtpEnabled: e.target.checked })} />
                  <span style={{ fontWeight: '600', color: company.smtpEnabled ? '#16a34a' : '#dc2626' }}>
                    {company.smtpEnabled ? '✅ Email Ativo' : '❌ Email Desativado'}
                  </span>
                </label>
              </div>

              <div style={ST.section}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px' }}>🔧 Servidor SMTP</div>
                <div style={ST.grid}>
                  <div style={ST.field}>
                    <label style={ST.label}>Servidor SMTP *</label>
                    <input style={ST.input} value={company.smtpHost || ''} onChange={e => setCompany({ ...company, smtpHost: e.target.value })}
                      placeholder="smtp.gmail.com / smtp.office365.com" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Porta *</label>
                    <input style={ST.input} type="number" value={company.smtpPort || 587} onChange={e => setCompany({ ...company, smtpPort: parseInt(e.target.value) })} />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Utilizador / Email *</label>
                    <input style={ST.input} value={company.smtpUser || ''} onChange={e => setCompany({ ...company, smtpUser: e.target.value })}
                      placeholder="email@empresa.pt" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Password *</label>
                    <input style={ST.input} type="password" value={company.smtpPassword || ''} onChange={e => setCompany({ ...company, smtpPassword: e.target.value })}
                      placeholder="••••••••" />
                  </div>
                </div>
              </div>

              <div style={ST.section}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px' }}>✉️ Remetente</div>
                <div style={ST.grid}>
                  <div style={ST.field}>
                    <label style={ST.label}>Email Remetente</label>
                    <input style={ST.input} type="email" value={company.smtpFromEmail || ''} onChange={e => setCompany({ ...company, smtpFromEmail: e.target.value })}
                      placeholder="noreply@empresa.pt" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>Nome Remetente</label>
                    <input style={ST.input} value={company.smtpFromName || ''} onChange={e => setCompany({ ...company, smtpFromName: e.target.value })}
                      placeholder="GyMag" />
                  </div>
                  <div style={ST.field}>
                    <label style={ST.label}>SSL / TLS</label>
                    <label style={ST.toggle}>
                      <input type="checkbox" checked={company.smtpEnableSsl !== false}
                        onChange={e => setCompany({ ...company, smtpEnableSsl: e.target.checked })} />
                      <span>{company.smtpEnableSsl !== false ? 'SSL Ativo' : 'SSL Desativo'}</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Test email */}
              <div style={{ ...ST.section, padding: '16px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px dashed var(--border-color)' }}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px', marginBottom: '12px' }}>🧪 Testar Configuração</div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ ...ST.field, flex: 1, minWidth: '200px' }}>
                    <label style={ST.label}>Email de Teste</label>
                    <input style={ST.input} type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                      placeholder="seu@email.com" />
                  </div>
                  <button className="btn btn-outline" onClick={handleTestEmail} disabled={testing}
                    style={{ height: '38px', whiteSpace: 'nowrap' }}>
                    {testing ? '⏳ A enviar...' : '📤 Enviar Email de Teste'}
                  </button>
                </div>
              </div>

              <button className="btn btn-primary" onClick={handleSaveCompany} disabled={saving} style={ST.saveBtn}>
                {saving ? 'A guardar...' : '💾 Guardar Configurações de Email'}
              </button>
            </div>
          )}

          {/* EMAIL TEMPLATES TAB */}
          {activeTab === 'email-templates' && company && (
            <div>
              <h3 style={ST.sectionTitle}>📨 Templates de Email</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
                Personalize os emails com tags dinâmicas. Os emails suportam HTML para formatação avançada.
              </p>

              {/* Email Template 1: Notificação */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px', marginBottom: '8px' }}>📢 Email Notificação (Geral)</div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', display: 'block' }}>Assunto do Email</label>
                  <input type="text" style={ST.input} value={company.emailSubjectNotification || ''}
                    onChange={e => setCompany({ ...company, emailSubjectNotification: e.target.value })}
                    placeholder="Nova notificação em {empresa}" />
                </div>
                <TemplateEditor
                  value={company.emailTemplateNotification || ''}
                  onChange={value => setCompany({ ...company, emailTemplateNotification: value })}
                  availableTags={[
                    { tag: '{nome}', label: 'Nome', desc: 'Nome do utilizador' },
                    { tag: '{empresa}', label: 'Empresa', desc: 'Nome da empresa' },
                    { tag: '{email}', label: 'Email', desc: 'Email do utilizador' },
                  ]}
                  placeholder="Olá, tem uma nova notificação..."
                  type="email"
                />
              </div>

              {/* Add other email templates similarly... (Document, Booking, Plan, Credit) */}

              <button className="btn btn-primary" onClick={handleSaveCompany} disabled={saving} style={ST.saveBtn}>
                {saving ? 'A guardar...' : '💾 Guardar Templates Email'}
              </button>
            </div>
          )}

          {/* SMS TAB */}
          {activeTab === 'sms' && company && (
            <div>
              <h3 style={ST.sectionTitle}>📱 Configurações de SMS</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                As mensagens saem pelo MagSend, configurado no servidor. Aqui liga ou desliga o envio.
              </p>

              {/* Enable toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', borderRadius: '10px', marginBottom: '20px',
                background: company.smsEnabled ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.05)',
                border: `1px solid ${company.smsEnabled ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.2)'}` }}>
                <label style={{ ...ST.toggle, flex: 1 }}>
                  <input type="checkbox" checked={company.smsEnabled || false}
                    onChange={e => setCompany({ ...company, smsEnabled: e.target.checked })} />
                  <span style={{ fontWeight: '600', color: company.smsEnabled ? '#16a34a' : '#dc2626' }}>
                    {company.smsEnabled ? '✅ SMS Ativo' : '❌ SMS Desativado'}
                  </span>
                </label>
              </div>

              <div style={ST.section}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px' }}>📡 Por onde saem</div>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                  SMS e WhatsApp saem pelo MagSend. A credencial vive no servidor, não aqui:
                  não há nada para importar nem tokens para guardar neste ecrã.
                </p>
                <div style={ST.grid}>
                  <div style={ST.field}>
                    <label style={ST.label}>Nome do remetente</label>
                    <input style={ST.input} value={company.smsSenderId || ''}
                      onChange={e => setCompany({ ...company, smsSenderId: e.target.value })}
                      placeholder="GyMag" />
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      É o nome que fica no histórico de envios.
                    </span>
                  </div>
                </div>
              </div>

              <button className="btn btn-primary" onClick={handleSaveCompany} disabled={saving} style={ST.saveBtn}>
                {saving ? 'A guardar...' : '💾 Guardar Configurações SMS'}
              </button>
            </div>
          )}

          {/* SMS TEMPLATES TAB */}
          {activeTab === 'sms-templates' && company && (
            <div>
              <h3 style={ST.sectionTitle}>💬 Templates de Mensagens SMS</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
                Personalize as mensagens SMS com tags dinâmicas. Clique nas tags para inserir.
              </p>

              {/* SMS Template 1: Notificação */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ ...ST.sectionTitle, fontSize: '14px', marginBottom: '8px' }}>📢 Envio Notificação (Geral)</div>
                <TemplateEditor
                  value={company.smsTemplateNotification || ''}
                  onChange={value => setCompany({ ...company, smsTemplateNotification: value })}
                  availableTags={[
                    { tag: '{nome}', label: 'Nome', desc: 'Nome do utilizador' },
                    { tag: '{empresa}', label: 'Empresa', desc: 'Nome da empresa' },
                  ]}
                  placeholder="Olá, tem uma nova notificação."
                  type="sms"
                />
              </div>

              {/* Add other SMS templates similarly... */}

              <button className="btn btn-primary" onClick={handleSaveCompany} disabled={saving} style={ST.saveBtn}>
                {saving ? 'A guardar...' : '💾 Guardar Templates SMS'}
              </button>
            </div>
          )}

          {/* SMS PARAMS TAB */}
          {activeTab === 'sms-params' && company && (
            <div>
              <h3 style={ST.sectionTitle}>⚙️ Parâmetros de Envio SMS</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
                Configure quando as mensagens SMS devem ser enviadas automaticamente.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{ ...ST.toggle, padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  <input type="checkbox" checked={company.smsParamSendDocuments || false}
                    onChange={e => setCompany({ ...company, smsParamSendDocuments: e.target.checked })} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '13px' }}>📄 Enviar SMS Documentos</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Notifica quando faturas/recibos são emitidos</div>
                  </div>
                </label>

                {/* Add other SMS params similarly... */}
              </div>

              <button className="btn btn-primary" onClick={handleSaveCompany} disabled={saving} style={ST.saveBtn}>
                {saving ? 'A guardar...' : '💾 Guardar Parâmetros SMS'}
              </button>
            </div>
          )}

          {/* LAYOUTS TAB - O código do layouts designer que já tens */}
          {activeTab === 'layouts' && (
            <div>
              <h3 style={ST.sectionTitle}>📐 Layouts de Documentos</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Crie e personalize layouts para faturas, recibos e outros documentos.
              </p>
              {/* Aqui vais meter o código do layouts designer que já tens */}
              <p>Layouts de documentos (código existente)</p>
            </div>
          )}

          {/* NOTIFICATIONS TAB */}
          {activeTab === 'notifications' && (
            <div>
              <h3 style={ST.sectionTitle}>🔔 Notificações</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{ ...ST.toggle, padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <input type="checkbox" checked={notifications.email} onChange={() => handleToggleNotification('email')} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600' }}>📧 Notificações por Email</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Receber confirmações e lembretes por email</div>
                  </div>
                </label>

                <label style={{ ...ST.toggle, padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <input type="checkbox" checked={notifications.push} onChange={() => handleToggleNotification('push')} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600' }}>📱 Notificações Push</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Receber notificações no browser</div>
                  </div>
                </label>

                <label style={{ ...ST.toggle, padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <input type="checkbox" checked={notifications.bookingReminders} onChange={() => handleToggleNotification('bookingReminders')} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600' }}>⏰ Lembretes de Reservas</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Receber lembrete 1 hora antes do treino</div>
                  </div>
                </label>

                <label style={{ ...ST.toggle, padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <input type="checkbox" checked={notifications.weeklyDigest} onChange={() => handleToggleNotification('weeklyDigest')} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600' }}>📊 Resumo Semanal</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Receber estatísticas semanais</div>
                  </div>
                </label>

                <label style={{ ...ST.toggle, padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <input type="checkbox" checked={notifications.promotions} onChange={() => handleToggleNotification('promotions')} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600' }}>🎁 Promoções</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Receber ofertas e novidades</div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* ACCOUNT TAB */}
          {activeTab === 'account' && (
            <div>
              <h3 style={ST.sectionTitle}>👤 Dados da Conta</h3>
              <p>Informações da sua conta pessoal</p>
            </div>
          )}

        </div>
      </div>

    </div>
  );
};

export default SettingsPageFull;
